import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Users, Zap } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1658956326341-af435b65c090?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjByZWQlMjBsaXF1aWQlMjBtZXRhbHxlbnwwfHx8fDE3NzY2Mzc1Njh8MA&ixlib=rb-4.1.0&q=85";

export default function Hero({ stats, discordUrl, liveTournamentId, hasLive, showStats = true }) {
  const bracketTarget = liveTournamentId ? `/tournament/${liveTournamentId}/bracket` : "/tournaments";
  return (
    <section className="relative overflow-hidden">
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "saturate(1.1) contrast(1.05)",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/70 via-black/80 to-[#050505]" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_50%_at_50%_40%,rgba(230,0,0,0.25),transparent_70%)]" />

      <div className="max-w-7xl mx-auto px-6 pt-20 pb-28 md:pt-28 md:pb-36">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl"
        >
          <h1 className="font-display font-black text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tighter mb-6">
            <span className="block text-white">SPICY</span>
            <span className="block shimmer-text">COMMUNITY</span>
          </h1>

          <p className="text-lg md:text-xl text-zinc-300 max-w-2xl mb-10 leading-relaxed">
            The ultimate competitive gaming and tournament community. Watch
            live brackets, track your favourite teams, and battle for glory.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href={discordUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-join-community"
              className="btn-liquid btn-liquid-lg pulse-glow"
            >
              Join Community <ArrowRight size={18} />
            </a>
            <Link
              to={bracketTarget}
              data-testid="hero-view-bracket"
              className="btn-ghost-glass btn-liquid-lg"
            >
              <Zap size={18} /> View Live Bracket
            </Link>
          </div>
        </motion.div>

        {/* Stats strip */}
        {showStats && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
          >
          {[
            { label: "Prize Pool", value: `$${(stats?.prize || 0).toLocaleString()}`, icon: Trophy },
            { label: "Teams", value: stats?.teams || 0, icon: Users },
            { label: "Matches", value: stats?.matches || 0, icon: Zap },
            { label: "Live Now", value: stats?.live ? "ON AIR" : "—", icon: null, live: stats?.live },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className="glass-dark rounded-xl p-5 relative overflow-hidden"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">
                  {s.label}
                </span>
                {s.live && <span className="live-dot" />}
              </div>
              <div className="font-display font-bold text-2xl md:text-3xl">
                {s.value}
              </div>
            </div>
          ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
